import React, { useEffect, useRef } from "react";
import {
  ShieldCheck,
  Users,
  CheckCircle,
  Database,
  PieChart,
  ClipboardCheck,
  Zap,
  FileSignature,
  ChartBar,
  Download,
  University,
  FileText,
  Globe
} from "lucide-react";
import SEO from "../components/SEO";
import "./UNICodePage.css";

const UNICodePage = () => {
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-fancy-in-view');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.fancy-reveal-scroll');
    hiddenElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 15;
    const rotateY = (centerX - x) / 15;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

    // Gloss shimmer
    const shimmer = card.querySelector('.fancy-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.4) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const shimmer = card.querySelector('.fancy-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `transparent`;
    }
  };

  const modules = [
    {
      title: "Masters Module",
      icon: <Database size={32} />,
      img: "./images/Unicode-Masters.png",
      badge: "Core Data",
      points: [
        "Manage academic years and semesters",
        "Define departments, UG/PG programs",
        "Map subjects to courses/semesters",
        "Maintain affiliated college master data",
        "Allocate subject papers to exam centers"
      ]
    },
    {
      title: "Pre-Examination",
      icon: <FileSignature size={32} />,
      img: "./images/Pre-Exam.png",
      badge: "Preparation",
      points: [
        "Import student data & verify eligibility",
        "Generate unique exam roll numbers",
        "Online registration for Regular/Supplementary",
        "Digital hall tickets with QR codes",
        "Real-time absentee entry via web/mobile"
      ]
    },
    {
      title: "Post-Examination",
      icon: <ClipboardCheck size={32} />,
      img: "./images/Post-exam.png",
      badge: "Verification",
      points: [
        "Secure OMR identification separation",
        "Decode roll numbers using high-speed scanners",
        "Verify manually filled absentee sheets",
        "Optical answer verification & trust scoring",
        "Upload internal & practical assessments"
      ]
    },
    {
      title: "Results & Analytics",
      icon: <PieChart size={32} />,
      img: "./images/Results.png",
      badge: "Publication",
      points: [
        "Auto-merge all evaluation components",
        "Configurable pass/fail & grading rules",
        "Marks Memo & Provisional Certificates",
        "Result publication on web & mobile app",
        "Performance Dashboards (Pass %, Trends)"
      ]
    }
  ];

  return (
    <div className="premium-unicode-wrapper">
      <SEO
        title="UNIcode — Complete University Examination Suite | DBS"
        description="UNIcode manages end-to-end university exam workflows across India: pre-exam registration, hall ticket generation, valuation, OMR scoring, and instant result publication."
        keywords="UNIcode, UNIcode exam suite, university examination automation software, hall ticket generator, results publication software, DBS UNIcode"
        canonical="https://dbasesolutions.in/unicode"
        ogImage="/og-unicode.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
          { name: "UNIcode Exam Suite", url: "/unicode" }
        ]}
      />
      {/* Immersive Background */}
      <div className="unicode-galaxy-bg">
        <div className="unicode-orb orb-u1"></div>
        <div className="unicode-orb orb-u2"></div>
        <div className="unicode-orb orb-u3"></div>
        <div className="data-particles">
          {[...Array(25)].map((_, i) => (
            <div key={i} className={`particle-u up${i}`}></div>
          ))}
        </div>
      </div>

      <div className="unicode-content-root">
        <header className="unicode-hero-header fancy-reveal-scroll">
          <div className="hero-status-tag">
            <Zap size={14} className="tag-glow" />
            <span>Automating Academic Lifecycle</span>
          </div>
          <div className="logo-box-creative">
            <img src="./images/UNICode.png" alt="UNICode Logo" className="logo-3d-drift" />
          </div>
          <h1 className="unicode-main-title">
            UNIcode <span className="gradient-shimmer-u">Exam</span> Suite
          </h1>
          <p className="unicode-hero-subtitle">
            A comprehensive academic examination management system designed to streamline and automate the entire lifecycle for universities with 100% precision.
          </p>

          <div className="unicode-hero-deco">
            <University className="deco-u item-u1" />
            <FileText className="deco-u item-u2" />
            <ShieldCheck className="deco-u item-u3" />
          </div>
        </header>

        <section className="unicode-overview-section fancy-reveal-scroll">
          <div className="overview-glass-box">
            <div className="overview-header-u">
              <Globe size={24} className="spin-u" />
              <h2>System Overview</h2>
            </div>
            <p>
              DBS UNIcode is a comprehensive academic examination management system designed to streamline and automate the entire examination lifecycle for universities. It encompasses four integrated modules—Masters, Pre-Examination, Post-Examination, and Result Processing—ensuring seamless coordination between academic planning, student registrations, examination execution, and result publication. The system manages academic years, courses, and subject structures while maintaining centralized control over examination centers and paper allocations. From student enrollment and hall ticket generation to OMR sheet handling and real-time absentee tracking, every stage is digitized for accuracy and efficiency. Post-examination processes such as scanning, verification, error correction, and internal mark validations are handled with precision, while the result module merges internal and external marks to generate official certificates, grade cards, and analytical dashboards
            </p>
            <p>
              DBS UNIcode not only reduces manual intervention but also minimizes errors, improves data accuracy, and ensures timely execution of each examination phase. It supports configurable workflows, role based access, and integration with external academic or admission systems. The platform facilitates compliance with academic regulations and provides actionable analytics to aid decision-making at institutional and university levels. With scalable architecture and mobile-ready features, UNIcode empowers universities to manage large volumes of examination data securely and efficiently, making the entire process seamless, transparent, and student-centric.
            </p>
          </div>
        </section>

        <section className="unicode-modules-grid">
          <div className="unicode-section-title fancy-reveal-scroll">
            <h2 className="title-u">Integrated <span className="text-secondary-u">Modules</span></h2>
            <div className="line-u"></div>
          </div>

          <div className="modules-flex-container">
            {modules.map((module, index) => (
              <div
                key={index}
                ref={el => cardRefs.current[index] = el}
                className="unicode-module-card fancy-reveal-scroll"
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => handleMouseLeave(index)}
              >
                <div className="fancy-card-shimmer"></div>
                <div className="card-inner-u">
                  <div className="card-top-u">
                    <div className="icon-box-u">
                      {module.icon}
                    </div>
                    <span className="badge-u">{module.badge}</span>
                  </div>
                  <h3 className="card-heading-u">{module.title}</h3>
                  <div className="card-image-u">
                    <img src={module.img} alt={module.title} className="module-thumbnail" />
                  </div>
                  <ul className="points-list-u">
                    {module.points.map((p, i) => (
                      <li key={i}><CheckCircle size={14} className="check-u" /> {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="unicode-advanced-fusion fancy-reveal-scroll">
          <div className="fusion-container-u">
            <div className="fusion-left-u">
              <h2 className="fusion-title-u">Advanced Capabilities</h2>
              <div className="fusion-grid-u">
                <div className="fusion-card-u">
                  <ShieldCheck size={20} className="f-icon" />
                  <p>Secure student ID separation</p>
                </div>
                <div className="fusion-card-u">
                  <Zap size={20} className="f-icon" />
                  <p>High-speed recognition scoring</p>
                </div>
                <div className="fusion-card-u">
                  <Users size={20} className="f-icon" />
                  <p>Real-time absentee validation</p>
                </div>
                <div className="fusion-card-u">
                  <ChartBar size={20} className="f-icon" />
                  <p>SGPA/CGPA result publication</p>
                </div>
              </div>
            </div>
            <div className="fusion-right-u">
              <div className="gfx-orb-u">
                <img src="./images/campusimages/result-processing.png" alt="Analytics" className="floating-gfx-u" />
              </div>
            </div>
          </div>
        </section>

        <footer className="unicode-cta-footer fancy-reveal-scroll">
          <div className="cta-glass-u">
            <h3>Ready to transform your university?</h3>
            <p>Experience the most reliable examination suite in the industry.</p>
            <div className="cta-btns-u">
              <button className="btn-plasma-u">Download Brochure <Download size={18} /></button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default UNICodePage;
